<template>
	<div data-component="edit-covering-conditions-modal">
		<div data-element="cards-list">
			<div data-element="table-label">
				Actions the bots (as 2nd player) take, if user <b>LEADS</b> certain cards:
			</div>
			<validation-observer
				data-element="form"
				tag="form"
				ref="form"
			>
				<covering-conditions-table
					v-for="(condition, key) in getConditions"
					:key="key"
					:index="key"
					:deal="deal"
					:condition="condition"
					:lead="getLead(key)"
					:lefty="getLefty(key)"
					@onCardSelection="handleCardSelection"
					@onClear="handleClear"
					@onDelete="handleDelete"
					@onAddCondition="handleAddCondition"
				/>
			</validation-observer>
		</div>
		<actions
			:actions="getActions"
			:isWaiting="isWaiting"
			:isDisabled="getIsDisabled"
			@actionClick="onActionClick"
		/>
	</div>
</template>

<script>

	import CoveringConditionsTable from '@/components/dealLibrary/CoveringConditionsTable';
	import Actions from '@/components/ui/Actions';
	import api from '@/services/api';
	import actionClick from '@/mixins/actionClick';
	import invalidFormMessage from '@/mixins/invalidFormMessage';
	import { uppercaseObjectKeys } from '@/helpers';
	import { SEATS } from '@/consts';
	import { Game } from '@bridge128/bridge128-game-logic-lib';

	export default {
		components: {
			CoveringConditionsTable,
			Actions
		},
		mixins: [actionClick, invalidFormMessage],
		props: {
			deal: {
				type: Object,
				default: undefined
			}
		},
		data: () => ({
			openingLead: undefined,
			isWaiting: undefined,
			conditions: []
		}),
		computed: {
			getGameData () {
				if (this.deal) {
					return Game.fromState(this.deal.deal);
				}
				return null;
			},
			getDummyPlayer () {
				if (this.getGameData) {
					return this.getGameData.dummy;
				}
				return null;
			},
			getDeclarer () {
				return this.deal?.declarer;
			},
			getFirstPair () {
				return this.getLeadAndLefty(this.getDeclarer);
			},
			getSecondPair () {
				return this.getLeadAndLefty(this.getDummyPlayer?.name);
			},
			getPairsMap () {
				return {
					0: this.getFirstPair,
					1: this.getSecondPair
				};
			},
			getConditions () {
				if (this.conditions?.length) {
					return this.conditions;
				}
				return [];
			},
			getIsDisabled  () {
				return undefined;
			},
			getFilteredConditions () {
				if (this.conditions?.length) {
					return this.conditions.map((item, index) => {
						return this.filterCondition(item, index);
					}).filter(item => item.length);
				}
				return [];
			},
			getActions () {
				return {
					primary: [
						{
							text: 'Save',
							btnId: 'save',
							actionId: 'handleSave'
						}
					],
					secondary: [
						{
							text: 'Cancel',
							actionId: 'close'
						}
					]
				};
			}
		},
		watch: {
			'deal.ddsCoveringConditions': {
				handler (conditions) {
					this.populateCards(conditions);
				},
				deep: true,
				immediate: true
			}
		},
		created () {},
		methods: {
			close () {
				this.$emit('close');
			},
			getLeadAndLefty (leadSide) {
				if (leadSide) {
					const lead = SEATS[leadSide];
					const lefty = SEATS[lead.left];
					return [lead, lefty];
				}
				return [];
			},
			createEmptyCondition (pair) {
				const [lead, lefty] = pair;

				return {
					[lead.short]: [],
					[lefty.short]: []
				};
			},
			getLead (index) {
				const pair = this.getPairsMap[index];
				if (pair) {
					return pair[0];
				}
				return undefined;
			},
			getLefty (index) {
				const pair = this.getPairsMap[index];
				if (pair) {
					return pair[1];
				}
				return undefined;
			},
			filterCondition (item = [], conditionIndex) {
				return item.filter(trick => {
					const lead = this.getLead(conditionIndex);
					const lefty = this.getLefty(conditionIndex);
					const leadCards = trick[lead.short];
					const leftyCards = trick[lefty.short];
					return leadCards?.length > 0 && leftyCards?.length > 0;
				});
			},
			handleCardSelection (conditionIndex, trickIndex, card, column) {
				this.$set(this.conditions[conditionIndex][trickIndex], column.short, card);
			},
			handleAddCondition (conditionIndex) {
				const condition = this.conditions[conditionIndex];
				this.$set(this.conditions, conditionIndex, [
					...condition,
					{
						...this.createEmptyCondition(this.getPairsMap[conditionIndex])
					}
				]);
			},
			capitalizeSides (conditions) {
				return conditions.map(item => {
					return item.map(trick => {
						return uppercaseObjectKeys(trick || {});
					});
				});
			},
			populateCards (conditions) {
				if (conditions?.length === 0) {
					this.conditions = [
						[
							{ ...this.createEmptyCondition(this.getFirstPair) }
						],
						[
							{ ...this.createEmptyCondition(this.getSecondPair) }
						]
					];
				} else if (conditions?.length === 1) {
					// If the one pair is missing - we fill in the empty value by default (empty) condition
					const capitalizedConditions = this.capitalizeSides(conditions);
					const pairConditions = capitalizedConditions[0];
					const firstCondition = pairConditions[0];
					if (this.getFirstPair.every(pair => {
						return Boolean(firstCondition[pair.short]);
					})) {
						this.conditions = [
							pairConditions,
							[
								{ ...this.createEmptyCondition(this.getSecondPair) }
							]
						];
					} else if (this.getSecondPair.every(pair => {
						return Boolean(firstCondition[pair.short]);
					})) {
						this.conditions = [
							[
								{ ...this.createEmptyCondition(this.getFirstPair) }
							],
							pairConditions
						];
					}
				} else {
					this.conditions = this.capitalizeSides(conditions);
				}
			},
			async updateDeal () {
				const deal = await api.deals.updateDeal({
					dealId: this.$route.params.dealId,
					deal: {
						...this.deal,
						ddsCoveringConditions: this.getFilteredConditions
					}
				});
				if (this.deal && deal) {
					this.deal.ddsCoveringConditions = deal.ddsCoveringConditions;
					return true;
				} else {
					console.error('Something went wrong. Empty response received.');
				}
			},
			async handleSave () {
				const valid = await this.$refs.form.validate();
				if (!valid) {
					return;
				}

				this.isWaiting = 'save';

				const dealResult = await this.updateDeal();

				if (dealResult) {
					this.$store.commit('ui/showNotification', {
						notification: 'Successfully saved'
					});
				}
				this.isWaiting = undefined;
			},
			handleClear (conditionIndex, trickIndex) {
				this.conditions = this.conditions.map((condition, i) => {
					if (conditionIndex === i) {
						return condition.map((trick, k) => {
							if (trickIndex === k) {
								return {
									...this.createEmptyCondition(this.getPairsMap[conditionIndex])
								};
							}

							return trick;
						});
					}
					return condition;
				});
			},
			handleDelete (conditionIndex, trickIndex) {
				if (trickIndex === 0) {
					return;
				}
				const condition = this.conditions[conditionIndex];
				this.$set(this.conditions, conditionIndex, condition.filter((_, i) => trickIndex !== i));
			}
		}
	};
</script>

<style lang="scss" scoped>

	[data-component='edit-covering-conditions-modal'] {
		[data-element='form'] {
			margin: 0;
		}

		[data-element='cards-list'] {
			margin-bottom: rem(28);

			[data-element='table-label'] {
				display: block;
				font-size: rem(18);
				line-height: 1;
				margin-bottom: rem(12);
				font-weight: 700;

				&[data-required] {
					&:after {
						margin: 0;
						content: '*';
						display: inline;
						color: $c-red;
					}
				}

				&[data-error] {
					color: $c-red;
				}
			}

		}

		::v-deep [data-component='actions'] {
			padding: 0 0 rem(10) 0;

      @media (max-width: 1279px) {
        flex-direction: column;
        gap: 12px;

        [data-element="actions-primary"] {
          margin: 0;
          width: 100%;
          justify-content: space-between;

          [data-component="btn"] {
            margin: 0
          }
        }

        [data-element="actions-secondary"] {
          margin: 0;
          width: 100%;
          justify-content: space-between;

          [data-component="btn"] {
            margin: 0;
            width: 100%
          }
        }
      }
		}
	}

</style>
